import React, { useMemo, useState } from "react";
import Lottie from "lottie-react";
import { toIso3 } from "../../../utils/iso2to3";

import geoAnim from "../../../images/geo-lottie.json";
import industryAnim from "../../../images/factory-lottie.json";

const EASE = [0.22, 1, 0.36, 1];
const LAYOUT_TRANSITION = { duration: 0.28, ease: EASE };

function geoLevelFromCountryRisk(r) {
  if (!r?.iso3) return null;

  const defaultSourceKeys = Array.isArray(r.defaultSourceKeys) ? r.defaultSourceKeys : [];
  const hasDefaultSourceKey = (key) => defaultSourceKeys.includes(key);

  if (r.isBlack) return "high";
  if (r.isOfac) return "high";
  if (hasDefaultSourceKey("baselScoreHigh")) return "high";
  if (Number(r.baselScore) > 7) return "high";
  if (String(r.customTier || "").toLowerCase() === "high") return "high";

  if (r.isGrey) return "medium";
  if (r.isTaxHaven) return "medium";
  if (r.isEuHighRisk || hasDefaultSourceKey("euHighRisk")) return "medium";
  if (hasDefaultSourceKey("baselScore") || hasDefaultSourceKey("baselScoreMedium")) return "medium";
  if (Number(r.baselScore) >= 5.5) return "medium";
  if (String(r.customTier || "").toLowerCase() === "medium") return "medium";

  return "low";
}

function maxGeoLevel(a, b) {
  const order = { low: 1, medium: 2, high: 3 };
  const aa = a && order[a] ? order[a] : 0;
  const bb = b && order[b] ? order[b] : 0;
  const top = Math.max(aa, bb);
  return top === 3 ? "high" : top === 2 ? "medium" : top === 1 ? "low" : null;
}

function badgeClassForCpi(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return "badge badgeStandard badgeKycGrey";
  if (s >= 70) return "badge badgeStandard badgeKycGreen";
  if (s >= 40) return "badge badgeStandard badgeKycYellow";
  return "badge badgeStandard badgeKycRed";
}

function badgeClassForBasel(score) {
  const s = Number(score);
  if (!Number.isFinite(s)) return "badge badgeStandard badgeKycGrey";
  if (s > 7) return "badge badgeStandard badgeKycRed";
  if (s >= 5.5) return "badge badgeStandard badgeKycYellow";
  return "badge badgeStandard badgeKycGreen";
}

function countryRiskBadges(r) {
  const iso3 = r?.iso3 || "";
  if (!iso3) {
    return [<span key="none" className="badge badgeStandard badgeKycGrey">No country selected</span>];
  }

  const isEuHighRisk =
    r?.isEuHighRisk === true ||
    (Array.isArray(r?.defaultSourceKeys) && r.defaultSourceKeys.includes("euHighRisk"));
  const customTier = String(r?.customTier || "").toLowerCase();
  const customTierClass =
    customTier === "high"
      ? "badgeKycRed"
      : customTier === "medium"
      ? "badgeKycYellow"
      : customTier === "standard" || customTier === "low"
      ? "badgeKycGreen"
      : "";

  return [
    <span key="fatf" className={`badge badgeStandard ${r.isBlack ? "badgeKycRed" : r.isGrey ? "badgeKycYellow" : "badgeKycGreen"}`}>
      {r.isBlack ? "FATF: Black list" : r.isGrey ? "FATF: Grey list" : "FATF"}
    </span>,
    <span key="ofac" className={`badge badgeStandard ${r.isOfac ? "badgeKycYellow" : "badgeKycGreen"}`}>
      {r.isOfac ? "OFAC: Active program" : "OFAC"}
    </span>,
    <span key="tax" className={`badge badgeStandard ${r.isTaxHaven ? "badgeKycYellow" : "badgeKycGreen"}`}>
      {r.isTaxHaven ? "EU Tax haven: Listed" : "EU Tax haven"}
    </span>,
    <span key="eu" className={`badge badgeStandard ${isEuHighRisk ? "badgeKycYellow" : "badgeKycGreen"}`}>
      {isEuHighRisk ? "EU High-risk: Listed" : "EU High-risk"}
    </span>,
    <span key="cpi" className={badgeClassForCpi(r.cpiScore)}>
      CPI: {Number.isFinite(Number(r.cpiScore)) ? Number(r.cpiScore) : "n/a"}
    </span>,
    <span key="basel" className={badgeClassForBasel(r.baselScore)}>
      Basel AML: {Number.isFinite(Number(r.baselScore)) ? Number(r.baselScore).toFixed(1) : "n/a"}
    </span>,
    customTier ? (
      <span key="custom" className={`badge badgeStandard ${customTierClass}`}>
        Custom list: {customTier === "standard" ? "Standard" : customTier}
      </span>
    ) : (
      <span
        key="custom"
        className="badge badgeStandard"
        style={{ backgroundColor: "#9BB1A5", color: "#fff" }}
      >
        Custom list
        <i className="bi bi-check-circle-fill" style={{ marginLeft: 5 }} />
      </span>
    ),
  ];
}

function gaugeModel(level) {
  if (level === "low") return { pct: 0.22, label: "Standard", tone: "low" };
  if (level === "medium") return { pct: 0.5, label: "Medium", tone: "medium" };
  if (level === "high") return { pct: 0.82, label: "High", tone: "high" };
  return { pct: 0, label: "—", tone: "na" };
}

function Gauge({ level, rightIcon = null }) {
  const { pct, label, tone } = gaugeModel(level);

  const needleDeg = -150 + pct * 120;

  const cx = 60,
    cy = 60,
    r = 44;
  const startX = cx - r,
    startY = cy;
  const endX = cx + r,
    endY = cy;
  const arc = `M ${startX} ${startY} A ${r} ${r} 0 0 1 ${endX} ${endY}`;

  const arcLen = Math.PI * r;
  const dash = `${arcLen * pct} ${arcLen}`;

  return (
    <div className="kycRiskGauge">
      <div className="kycRiskGaugeBodySide">
        <div className="kycRiskGaugeLeftStack">
          <div className={`kycRiskGaugePill kycRiskGaugePill-${tone}`}>
            {label}
          </div>

          <svg
            viewBox="0 0 120 70"
            className="kycRiskGaugeSvg"
            aria-hidden="true"
          >
            <path d={arc} className="kycRiskGaugeArcBg" />
            <path
              d={arc}
              className={`kycRiskGaugeArcFill kycRiskGaugeArcFill-${tone}`}
              style={{ strokeDasharray: dash }}
            />
            <g transform={`translate(${cx} ${cy}) rotate(${needleDeg})`}>
              <rect
                x="0"
                y="-2.3"
                width="42"
                height="4.6"
                rx="2.3"
                className="kycRiskGaugeNeedle"
              />
            </g>
            <circle cx={cx} cy={cy} r="5.2" className="kycRiskGaugeHub" />
          </svg>
        </div>

        {rightIcon ? (
          <div className="kycRiskGaugeIconBig">{rightIcon}</div>
        ) : null}
      </div>
    </div>
  );
}

export default function KycViewRiskAssessment({ sectionNo = 0, form }) {
  // Same behaviour as the /kyc form section: details collapsed until asked for.
  const [showGeoDetails, setShowGeoDetails] = useState(false);
  const customer = form?.customer || {};
  const internalUse = form?.internalUse || {};
  const isEntity = customer.type === "entity";

  const isoIncorpOrNat = useMemo(
    () => toIso3(customer.nationalityOrIncorporationCountry),
    [customer.nationalityOrIncorporationCountry]
  );

  const isoDomicile = useMemo(
    () => (isEntity ? "" : toIso3(customer.country)),
    [customer.country, isEntity]
  );

  const geographySaved = internalUse?.geoRiskResult || null;
  const industrySaved = internalUse?.industryRiskResult || null;
  const uboCountryRisks = Array.isArray(geographySaved?.uboCountryRisks)
    ? geographySaved.uboCountryRisks
    : [];

  const geoRiskLevel = useMemo(() => {
    if (geographySaved?.geoRiskLevel) return geographySaved.geoRiskLevel;

    const riskA = geographySaved?.riskA || { iso3: isoIncorpOrNat };
    const riskB = geographySaved?.riskB || { iso3: isoDomicile };

    const geoLevelA = geoLevelFromCountryRisk(riskA);
    const geoLevelB = isEntity ? null : geoLevelFromCountryRisk(riskB);

    return isEntity ? geoLevelA : maxGeoLevel(geoLevelA, geoLevelB);
  }, [geographySaved, isoIncorpOrNat, isoDomicile, isEntity]);

  const industryLevel = useMemo(() => {
    const finalRisk = industrySaved?.final?.riskLevel;
    const autoRisk = industrySaved?.auto?.riskLevel;
    const effective = finalRisk ?? autoRisk ?? null;

    if (Number(effective) === 1) return "low";
    if (Number(effective) === 2) return "medium";
    if (Number(effective) === 3) return "high";
    return null;
  }, [industrySaved]);

  const highestUboCountryRisks = useMemo(() => {
    const maxLevel = uboCountryRisks.reduce((level, ubo) => maxGeoLevel(level, ubo?.geoLevel), null);
    if (!maxLevel) return [];
    if (maxLevel === "low") return [];
    return uboCountryRisks.filter((ubo) => ubo?.geoLevel === maxLevel);
  }, [uboCountryRisks]);

  const showStandardUboSummary =
    isEntity &&
    uboCountryRisks.length > 0 &&
    uboCountryRisks.reduce((level, ubo) => maxGeoLevel(level, ubo?.geoLevel), null) === "low";

  function renderUboCountrySide(label, r) {
    return (
      <div className="border rounded-3 p-2" style={{ background: "#FBFBFA" }}>
        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
          <span style={{ color: "#626F68", fontSize: 12, fontWeight: 700 }}>{label}</span>
          <span style={{ color: "#7D8D84", fontSize: 12, fontWeight: 600 }}>{r?.iso3 || "—"}</span>
        </div>
        <div className="d-flex flex-wrap gap-1">{countryRiskBadges(r)}</div>
      </div>
    );
  }

  function renderSavedCountryCard(label, r) {
    return (
      <div className="border rounded-3 p-3 mt-3" style={{ background: "#fff" }}>
        <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
          <span style={{ color: "#4C5465", fontSize: 13, fontWeight: 700 }}>{label}</span>
          <span style={{ color: "#7D8D84", fontSize: 12, fontWeight: 600 }}>{r?.iso3 || "—"}</span>
        </div>
        <div className="d-flex flex-wrap gap-1">{countryRiskBadges(r)}</div>
      </div>
    );
  }

  function renderUboGeoCard(ubo, idx) {
    return (
      <div key={ubo?.key || idx} className="border rounded-3 p-3 mb-2" style={{ background: "#fff" }}>
        <div className="d-flex justify-content-between align-items-start gap-3 mb-2">
          <div>
            <div style={{ color: "#4C5465", fontSize: 13, fontWeight: 700 }}>
              Highest-risk UBO: {ubo?.name || `UBO ${idx + 1}`}
            </div>
          </div>
        </div>
        <div className="row g-2">
          <div className="col-12 col-md-6">{renderUboCountrySide("Nationality", ubo?.nationalityRisk)}</div>
          <div className="col-12 col-md-6">{renderUboCountrySide("Domicile", ubo?.domicileRisk)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="card kycCard">
      <div className="kycSectionHead">
        <h5 className="kycSectionTitle">
          {sectionNo ? `${sectionNo} | ` : ""}Risk assessment
        </h5>
      </div>

      <div className="kycSectionBody">
        <div className="row g-3 align-items-start">
          <div className="col-12 col-lg-6">
            <div
              className="p-3 border rounded-3 kycRiskHeader"
              style={{ background: "#F6F4F2", minHeight: 118 }}
            >
              <div className="kycRiskHeaderLeft">
                <div>
                  <div className="fw-semibold" style={{ color: "#4C5465" }}>
                    Geography risk
                  </div>
                  <div className="small text-muted" style={{ fontWeight: 100 }}>
                    {isEntity
                      ? "Based on incorporation country"
                      : "Based on nationality and domicile"}
                  </div>
                </div>
                {isEntity ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary kycRiskDetailsToggle"
                    onClick={(e) => {
                      setShowGeoDetails((open) => !open);
                      e.currentTarget.blur();
                    }}
                  >
                    {showGeoDetails ? (
                      <>
                        <i className="bi bi-chevron-up me-1" />
                        Hide details
                      </>
                    ) : (
                      <>
                        <i className="bi bi-chevron-down me-1" />
                        Show details
                      </>
                    )}
                  </button>
                ) : null}
              </div>

              <div className="kycRiskHeaderRight">
                <div className="kycRiskHeaderGauge">
                  <Gauge
                    level={geoRiskLevel}
                    rightIcon={
                      <Lottie
                        animationData={geoAnim}
                        loop
                        autoplay
                        style={{ width: 85, height: 85 }}
                      />
                    }
                  />
                </div>
              </div>
            </div>

            {isEntity && showGeoDetails ? renderSavedCountryCard("Incorporation country", geographySaved?.riskA) : null}

            {isEntity && showGeoDetails && highestUboCountryRisks.length ? (
              <div className="mt-3">
                {highestUboCountryRisks.map((ubo, idx) => renderUboGeoCard(ubo, idx))}
              </div>
            ) : null}
            {showGeoDetails && showStandardUboSummary ? (
              <div className="border rounded-3 p-3 mt-3" style={{ background: "#fff" }}>
                <div style={{ color: "#4C5465", fontSize: 13, fontWeight: 700 }}>
                  Relevant UBOs
                </div>
                <div className="d-flex flex-wrap gap-1" style={{ marginTop: 8 }}>
                  <span className="badge badgeStandard badgeKycGreen">
                    No elevated risk identified
                    <i
                      className="bi bi-check-circle-fill"
                      style={{ marginLeft: 5 }}
                    />
                  </span>
                </div>
              </div>
            ) : null}
          </div>

          <div className="col-12 col-lg-6">
            <div
              className="p-3 border rounded-3 kycRiskHeader"
              style={{ background: "#F6F4F2", minHeight: 118 }}
            >
              <div className="kycRiskHeaderLeft">
                <div className="fw-semibold" style={{ color: "#4C5465" }}>
                  Industry risk
                </div>
                <div className="small text-muted" style={{ fontWeight: 100 }}>
                  Based on business activity categorization
                </div>
              </div>

              <div className="kycRiskHeaderRight">
                <div className="kycRiskHeaderGauge">
                  <Gauge
                    level={industryLevel}
                    rightIcon={
                      <Lottie
                        animationData={industryAnim}
                        loop
                        autoplay
                        style={{ width: 85, height: 85 }}
                      />
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
